import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Mail, Loader2, Check, ArrowRight } from "lucide-react";
import axios from "axios";
import { useTranslation } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "fwdco-newsletter-state"; // values: "subscribed" | "dismissed"
const SHOW_DELAY_MS = 8000;

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
}

export default function NewsletterPopup() {
  const { t, lang } = useTranslation();
  const n = t.newsletter;

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  // Show popup after delay (once per visitor unless they clear localStorage)
  useEffect(() => {
    let saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch (_) { /* ignore */ }
    if (saved === "subscribed" || saved === "dismissed") return;

    const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const close = (reason = "dismissed") => {
    setOpen(false);
    try { localStorage.setItem(STORAGE_KEY, reason); } catch (_) { /* ignore */ }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!isValidEmail(email)) {
      setError(n.invalidEmail);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter`, { email: email.trim(), language: lang, source: "popup" });
      setSubmitted(true);
      try { localStorage.setItem(STORAGE_KEY, "subscribed"); } catch (_) { /* ignore */ }
    } catch (err) {
      console.error("Newsletter signup failed:", err);
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) {
        setError(detail[0].msg);
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError(n.genericError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="newsletter-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4"
          data-testid="newsletter-popup"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label={n.close}
            onClick={() => close("dismissed")}
            className="absolute inset-0 bg-black/70 backdrop-blur-sm cursor-default"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="relative w-full max-w-md bg-[#141417] border border-[#2A2A2E] overflow-hidden shadow-2xl"
            style={{ borderRadius: "2px" }}
          >
            {/* Gold ambient glow */}
            <div className="absolute -top-24 -right-24 w-64 h-64 bg-[#C9A96E]/10 blur-[120px] rounded-full pointer-events-none" />

            {/* Close button */}
            <button
              type="button"
              onClick={() => close("dismissed")}
              className="absolute top-4 right-4 z-10 w-8 h-8 rounded-sm flex items-center justify-center text-[#B8B8C0] hover:text-[#F5F1E8] hover:bg-[#2A2A2E]/60 transition-colors"
              aria-label={n.close}
              data-testid="newsletter-close"
            >
              <X size={18} />
            </button>

            <div className="relative p-8 md:p-10">
              {submitted ? (
                <div className="text-center py-2">
                  <div className="w-14 h-14 rounded-sm bg-[#C9A96E]/10 border border-[#C9A96E]/30 flex items-center justify-center mx-auto mb-5">
                    <Check size={28} className="text-[#C9A96E]" />
                  </div>
                  <h3 className="fwd-heading mb-3" style={{ fontSize: "26px" }}>
                    {n.successTitle}
                  </h3>
                  <p className="fwd-body" style={{ fontSize: "17px" }}>
                    {n.successDesc}
                  </p>
                </div>
              ) : (
                <>
                  {/* Icon */}
                  <div className="w-12 h-12 rounded-sm bg-gradient-to-br from-[#C9A96E]/20 to-[#141417] border border-[#C9A96E]/30 flex items-center justify-center mb-5">
                    <Mail size={20} className="text-[#C9A96E]" strokeWidth={1.5} />
                  </div>

                  {/* Headline */}
                  <h3 className="fwd-heading mb-4" style={{ fontSize: "26px" }}>
                    {n.title}
                  </h3>

                  {/* Body */}
                  <p className="fwd-body mb-6">
                    {n.description}
                  </p>

                  {/* Form */}
                  <form onSubmit={handleSubmit} className="space-y-3" data-testid="newsletter-form" noValidate>
                    <input
                      type="email"
                      name="email"
                      value={email}
                      onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                      placeholder={n.emailPlaceholder}
                      autoComplete="email"
                      className="w-full h-12 bg-[#0A0A0B] border border-[#2A2A2E] rounded-sm px-4 text-[#F5F1E8] placeholder:text-[#B8B8C0]/60 focus:border-[#C9A96E]/50 focus:outline-none focus:ring-1 focus:ring-[#C9A96E]/20 transition-colors"
                      style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400, fontSize: "16px" }}
                      data-testid="newsletter-email-input"
                      disabled={submitting}
                    />

                    {error && (
                      <p
                        className="text-sm text-[#E07A5F]"
                        style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400 }}
                        data-testid="newsletter-error"
                      >
                        {error}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={submitting}
                      className="fwd-button w-full justify-center"
                      style={{ height: "48px", borderRadius: "2px" }}
                      data-testid="newsletter-submit"
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          {n.submit}
                          <ArrowRight size={16} className="fwd-arrow" />
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={() => close("dismissed")}
                      className="w-full text-center text-[#B8B8C0] hover:text-[#F5F1E8] transition-colors py-2"
                      style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 400, fontSize: "16px" }}
                      data-testid="newsletter-maybe-later"
                      aria-label={n.close}
                    >
                      {n.maybeLater}
                    </button>
                  </form>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
